import { create } from "zustand";
import { apiClient } from "@/lib/api";
import { User } from "@/types/user";
import { logger } from "@/lib/logger";
import { useProjectStore } from "./projectStore";
import { resetInventoryMode } from "@/hooks/useInventoryMode";

export interface YouTubeStats {
  subscriber_count: number;
  view_count: number;
  video_count: number;
  hidden_subscriber_count: boolean;
}

export interface YouTubeData {
  channel_id: string;
  title: string;
  custom_url?: string;
  thumbnail_url?: string;
  country?: string;
  stats?: YouTubeStats;
  last_updated?: string;
}

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  brand: any | null;
  isLoading: boolean;
  isCheckingAuth: boolean; // Add flag to prevent concurrent auth checks
  login: () => void;
  logout: () => void;
  checkAuth: () => Promise<User | null>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  isAuthenticated: !!localStorage.getItem("is_logged_in"),
  user: null,
  brand: null,
  isLoading: false, // No longer blocking on load
  isCheckingAuth: false, // Add the missing property

  login: () => {
    // Clear any existing authentication state before setting new login
    // BUT preserve Safari token if it exists since we just set it
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const safariToken = sessionStorage.getItem('safari_auth_token');
    
    localStorage.removeItem("access_token");
    sessionStorage.removeItem("access_token");
    
    // Only clear Safari token if we're not on Safari or if there's no token
    if (!isSafari || !safariToken) {
      sessionStorage.removeItem('safari_auth_token');
    } else {
      console.log("🍎 Safari: Preserving auth token during login state update");
    }
    
    localStorage.setItem("is_logged_in", "true");
    set({ isAuthenticated: true });

    // The placement-agent answer is PER ACCOUNT, so the previous account's answer must not survive
    // into this one. Nothing reloads the page between two sessions in a SPA.
    //
    // AFTER the two lines above, never before. Sitting ahead of them put a non-essential cache reset
    // in front of the statement that marks the session live: anything it threw would leave
    // is_logged_in unset, checkAuth would return null on the next route, and every guarded page would
    // bounce to home from a login that had otherwise succeeded.
    try {
      resetInventoryMode();
    } catch {
      /* a stale cached answer is a wrong destination; a failed login is no destination at all */
    }
    logger.info("User logged in successfully", {
      timestamp: new Date().toISOString(),
      action: "login",
      safari: isSafari,
      hasToken: !!safariToken
    });
  },

  logout: () => {
    const currentUser = get().user;
    localStorage.removeItem("is_logged_in");
    sessionStorage.removeItem('safari_auth_token');
    
    // Clear all possible authentication storage locations
    localStorage.removeItem("access_token");
    sessionStorage.removeItem("access_token");
    
    set({ isAuthenticated: false, user: null, brand: null });
    
    // Clear project store state
    useProjectStore.getState().resetDraft();
    useProjectStore.getState().setProject(null);
    
    // Clear onboarding v2 data
    localStorage.removeItem("onboarding_v2_data");

    // ...and the per-account placement-agent answer, for the same reason it is cleared on login.
    resetInventoryMode();

    logger.info("User logged out", {
      timestamp: new Date().toISOString(),
      action: "logout",
      userId: currentUser?._id,
      userEmail: currentUser?.email
    });
  },

  checkAuth: async () => {
    // A SECOND CALLER WAITS FOR THE FIRST, rather than being handed whatever `user` happens to be
    // right now. Both are "don't make two API calls", but the old version answered the question
    // wrongly: it returned get().user, which on a cold page load is still null because the first
    // call has not come back yet. The caller could not tell "not signed in" from "not known yet".
    //
    // AdminRoute is where that surfaced. App.tsx:186 and AdminRoute.tsx:13 both call this on mount,
    // App wins the race, and AdminRoute got null back, marked its check done, read user?.role as
    // undefined and redirected to "/". Deep-linking to ANY /admin URL bounced to the home page —
    // including /admin/brand-marketplace/match, which is the whole O3 read surface. It only appeared
    // to work when navigating in-app from a page that had already populated the store.
    if (get().isCheckingAuth) {
      await new Promise<void>((resolve) => {
        // Bounded, because a check that never clears its flag must not hang every guarded route
        // behind it. On timeout we fall through to the old behaviour of answering from the store.
        const timer = setTimeout(() => { unsubscribe(); resolve(); }, 8000);
        const unsubscribe = useAuthStore.subscribe((state) => {
          if (!state.isCheckingAuth) { clearTimeout(timer); unsubscribe(); resolve(); }
        });
      });
      return get().user;
    }

    // Check for both legacy login flag and brand token
    const isLoggedIn = !!localStorage.getItem("is_logged_in");
    const hasBrandToken = !!sessionStorage.getItem('safari_auth_token');
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    console.log("🔄 AuthStore: checkAuth called", {
      timestamp: new Date().toISOString(),
      isLoggedIn,
      hasBrandToken,
      isSafari,
      currentUser: get().user?._id,
      isCheckingAuth: get().isCheckingAuth
    });
    
    if (!isLoggedIn && !hasBrandToken) {
      console.log("🔒 AuthStore: No authentication tokens found");
      return null;
    }
    
    // Set checking flag to prevent concurrent calls
    set({ isCheckingAuth: true });
    
    // Only set loading if we don't have a user yet to prevent unmounting/flickering on refreshes
    if (!get().user) {
      set({ isLoading: true });
    }
    
    try {
       let newUser;
       
       // If we have a brand token, try fetching the brand profile first
       // This ensures we get the onboarding status correctly
       if (hasBrandToken) {
         try {
           console.log("🌐 AuthStore: Making API call to getBrandCurrentUser (brand token path)");
           const brandProfile = await apiClient.getBrandCurrentUser();
           console.log("✅ AuthStore: getBrandCurrentUser successful", {
             onboardingStatus: brandProfile.onboarding_status,
             nextStep: brandProfile.next_step
           });
           newUser = {
             ...brandProfile.user,
             brand_id: brandProfile.brand?._id || brandProfile.brand?.id,
             onboarding_status: brandProfile.onboarding_status,
             next_step: brandProfile.next_step,
             onboarding_completed: brandProfile.onboarding_complete
           };
           // Set the brand in the store if available
           if (brandProfile.brand) {
             set({ brand: brandProfile.brand });
           }
         } catch (e) {
           // For Safari, if brand profile fails, check if it's a 401 and clear the token
           if (isSafari && e instanceof Error && e.message.includes('401')) {
             console.warn("🍎 Safari: Brand profile 401 error, clearing token", e);
             sessionStorage.removeItem('safari_auth_token');
             throw e; // Re-throw to trigger logout flow
           }
           // Fallback to standard user fetch if brand profile fails but we might still have a valid session
           console.warn("❌ AuthStore: Brand profile fetch failed, falling back to standard user fetch", e);
           console.log("🌐 AuthStore: Making API call to getCurrentUser (fallback)");
           newUser = await apiClient.getCurrentUser();
           console.log("✅ AuthStore: getCurrentUser fallback successful");
         }
       } else {
         // Standard user fetch for non-brand or legacy flows
         console.log("🌐 AuthStore: Making API call to getCurrentUser (standard path)");
         newUser = await apiClient.getCurrentUser();
         console.log("✅ AuthStore: getCurrentUser successful", { role: newUser.role });
         
         // If it's a brand user or admin, try to fetch brand profile data
         if (newUser.role === 'brand_user' || newUser.role === 'admin') {
           try {
             console.log("🌐 AuthStore: Making additional API call to getBrandCurrentUser (brand user enrichment)");
             const brandProfile = await apiClient.getBrandCurrentUser();
             console.log("✅ AuthStore: Brand user enrichment successful");
             newUser = {
               ...newUser,
               brand_id: brandProfile.brand?._id || brandProfile.brand?.id,
               onboarding_status: brandProfile.onboarding_status,
               next_step: brandProfile.next_step,
               onboarding_completed: brandProfile.onboarding_complete
             };
             // Set the brand in the store if available
             if (brandProfile.brand) {
               set({ brand: brandProfile.brand });
             }
           } catch (e) {
             console.warn("❌ AuthStore: Failed to enrich brand user with profile data", e);
             // For brand_user role, this is more critical, so we should still mark them as a brand user
             if (newUser.role === 'brand_user') {
               console.log("🔧 AuthStore: Marking as brand user despite profile fetch failure");
               newUser = {
                 ...newUser,
                 onboarding_status: 'account_created',
                 next_step: 'company_info',
                 onboarding_completed: false
               };
             }
           }
        }
      }

      logger.debug("Auth check successful", {
         userId: newUser._id,
         userEmail: newUser.email,
         hasYouTubeConnection: !!(newUser as any).youtube_data,
         timestamp: new Date().toISOString()
       });
       
       // Always update to ensure subscribers are notified of changes (e.g. youtube connection)
       set({ isAuthenticated: true, user: newUser as any, isLoading: false, isCheckingAuth: false });
       
       // Ensure local storage flag is set for persistence across tabs
       if (!localStorage.getItem("is_logged_in")) {
         localStorage.setItem("is_logged_in", "true");
       }
       
       console.log("✅ AuthStore: checkAuth completed successfully");
       return newUser;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      logger.error("Auth check failed", error instanceof Error ? error : new Error(String(error)), {
        timestamp: new Date().toISOString(),
        errorMessage,
        isLoggedInLocalStorage: !!localStorage.getItem("is_logged_in"),
        isSafari,
        hasBrandToken
      });
      
      // Safari-specific error handling
      if (isSafari && errorMessage.includes('401')) {
        console.warn("🍎 Safari authentication failed - this may be due to cookie blocking. Consider using a different browser or adjusting Safari privacy settings.");
      }
      
      // Don't automatically log out - just clear the user data
      // The ProtectedRoute component will handle redirects if needed
      set({ user: null, isLoading: false, isCheckingAuth: false });
      
      // Only clear isAuthenticated and localStorage if it's a 401 (unauthorized)
      if (error instanceof Error && error.message.includes('401')) {
        localStorage.removeItem("is_logged_in");
        sessionStorage.removeItem('safari_auth_token');
        set({ isAuthenticated: false });
        logger.warn("User session expired - cleared authentication", {
          timestamp: new Date().toISOString(),
          action: "session_expired",
          isSafari
        });
      }
      console.log("❌ AuthStore: checkAuth failed, flag reset");
      return null;
    }
  }
}));